import React, { useCallback, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import List from "@mui/material/List";
import MuiListItem from "@mui/material/ListItem";
import MuiListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { Link } from "react-router-dom";
import { Icon, useMediaQuery, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import routes from "../roots/routes";
import protectedRoutes from "../roots/protectedRoutes";
import { useLayout } from "../../../../_context/DefaultProvider";
import useSmoothResponsiveFontSize from "../../../../../_hook/useSmoothResponsiveFontSize";

export const MenuListStyle = styled(({ istop, currentwidth, ...other }) => (
  <List {...other} />
))(({ istop, currentwidth }) => ({
  ...(istop && {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: `${currentwidth}px`,
    backgroundColor: "transparent",
    paddingRight: "100px",
  }),
}));

export const StyledListItem = styled(MuiListItem)(({ theme }) => ({
  margin: 0,
  padding: 0,
  width: "100%",
  backgroundColor: "transparent",
}));
export const StyledListItemButton = styled(
  ({ istop, isanimated = true, theme, customfontsize, ...other }) => (
    <MuiListItemButton {...other} />
  )
)(({ theme, customfontsize, istop, isanimated = true }) => ({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  justifyContent: "center",
  backgroundColor: "transparent",
  textDecoration: "none",
  "& .MuiListItemText-primary": {
    fontSize: `${customfontsize}px`,
  },
  ...(isanimated && {
    "&:before": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      width: 0,
      height: "2px",
      backgroundColor: istop
        ? theme.palette.white.main
        : theme.palette.primary.main,
      transform: "translateX(-100%)",
      transition: "transform 0.3s ease",
    },
    "&:hover:before": {
      width: "100%",
      transform: "translateX(0)",
    },
  }),
  "&:hover": {
    textDecoration: "none",
  },
}));

export const StyledIcon = styled(Icon)({});

const MenuList = (props) => {
  const { istop = false, isIcon = true, currentwidth } = props;
  const theme = useTheme();
  const session = { user: true };
  const status = "pending";
  const { drawerStates } = useLayout();
  const upRoutes = useRoutes(session, status);
  const customfontsize = useSmoothResponsiveFontSize({});

  useEffect(() => {}, [customfontsize]);

  return (
    <MenuListStyle istop={istop} currentwidth={currentwidth}>
      {upRoutes.map((item, index) => (
        <motion.div
          key={item.name}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          style={{ width: "100%", backgroundColor: "transparent" }}
        >
          <StyledListItem key={item.name}>
            <StyledListItemButton
              component={Link}
              to={item.route}
              istop={istop}
              customfontsize={customfontsize}
            >
              {isIcon && (
                <StyledIcon
                  style={{
                    color: istop
                      ? theme.palette.white.main
                      : theme.palette.primary.main,
                    fontSize: drawerStates?.isExpanded
                      ? `${2 * customfontsize}px`
                      : `${3 * customfontsize}px`,
                  }}
                >
                  {item.icon}
                </StyledIcon>
              )}
              {drawerStates?.isExpanded && (
                <ListItemText
                  primary={item.name}
                  sx={{
                    paddingLeft: ".5rem",
                    textWrap: "nowrap",
                    fontSize: `${customfontsize}px`,
                    color: istop
                      ? theme.palette.white.main
                      : theme.palette.text.dark,
                  }}
                />
              )}
            </StyledListItemButton>
          </StyledListItem>
        </motion.div>
      ))}
    </MenuListStyle>
  );
};

export default MenuList;

export const useRoutes = (session, status) => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const [upRoutes, setRoutes] = useState([
    { icon: "home", name: "Home", route: "/" },
  ]);

  const updateRoutes = useCallback(() => {
    const baseRoutes = [...routes, ...protectedRoutes];
    const newRoutes = session?.user ? baseRoutes : routes;

    setRoutes((prevRoutes) => {
      return JSON.stringify(prevRoutes) === JSON.stringify(newRoutes)
        ? prevRoutes
        : newRoutes;
    });
  }, [isMobileView, session?.user]);

  useEffect(() => {
    updateRoutes();
  }, [status, updateRoutes]);

  return upRoutes;
};
